// src/pages/PurchaseOrders.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  query,
  orderBy,
  doc,
  updateDoc,
  serverTimestamp,
  addDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { getAuth } from "firebase/auth";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useSearchParams } from "react-router-dom";

export default function PurchaseOrders() {
  const [pos, setPOs] = useState([]);
  const [selectedPO, setSelectedPO] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [attachments, setAttachments] = useState([]);
  const auth = getAuth();
  const storage = getStorage();

  const localUser = JSON.parse(localStorage.getItem("user") || "null");
  const [searchParams] = useSearchParams();
  const statusFilter = searchParams.get("status") || "all";

  const pendingOnly = useMemo(() => statusFilter === "pending", [statusFilter]);
  const canApproveReject = !pendingOnly;

  const canApprove = (user) => {
    if (!user) return false;
    return ["admin", "accounts"].includes(user.role);
  };

  const formatCreatedAt = (ts) => {
    if (!ts) return "—";
    if (ts.seconds) return new Date(ts.seconds * 1000).toLocaleString();
    if (typeof ts === "string") {
      const d = new Date(ts);
      return isNaN(d.getTime()) ? "—" : d.toLocaleString();
    }
    return "—";
  };

  const fetchPOs = async () => {
    try {
      setLoading(true);
      setError("");
      const q = query(collection(db, "purchaseOrders"), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const filtered = pendingOnly
        ? data.filter((po) => {
            const st = String(po.status || "").trim().toLowerCase();
            const received = po.orderReceived ?? po.received;
            const receivedBool = typeof received === "boolean" ? received : null;

            // Prefer boolean received flag when it exists.
            if (receivedBool === false) return true;
            if (receivedBool === true) return false;

            if (st.includes("pending")) return true;

            // If status clearly indicates a final state, exclude it.
            const isApproved = st === "approved";
            const isRejected = st === "rejected";
            const isCancelled = st === "cancelled" || st === "canceled";
            if (isApproved || isRejected || isCancelled) return false;

            // Otherwise (missing/unknown status), treat as pending.
            return true;
          })
        : data;
      setPOs(filtered);
    } catch (err) {
      console.error("fetchPOs error", err);
      setError("Failed to load POs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPOs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingOnly]);

  const openPO = (po) => setSelectedPO(po);
  const closePO = () => {
    setSelectedPO(null);
    setAttachments([]);
  };

  // ---------------- Attachments Upload ----------------
  const handleAttachmentUpload = async (files) => {
    if (!selectedPO) return;
    try {
      setBusy(true);
      const uploaded = [];
      for (const file of files) {
        const fileRef = ref(storage, `purchaseOrders/${selectedPO.id}/${file.name}`);
        await uploadBytes(fileRef, file);
        const url = await getDownloadURL(fileRef);
        uploaded.push({ name: file.name, url, uploadedAt: new Date().toISOString() });
      }
      const poRef = doc(db, "purchaseOrders", selectedPO.id);
      await updateDoc(poRef, {
        attachments: [...(selectedPO.attachments || []), ...uploaded],
        modifiedAt: serverTimestamp(),
      });
      setSelectedPO({
        ...selectedPO,
        attachments: [...(selectedPO.attachments || []), ...uploaded],
      });
      setAttachments([]);
    } catch (err) {
      console.error("Attachment upload error", err);
      setError("Upload failed");
    } finally {
      setBusy(false);
    }
  };

  // ---------------- Status Update (Approve/Reject) ----------------
  const updatePOStatus = async (poId, newStatus) => {
    if (!window.confirm(`Are you sure you want to ${newStatus} this PO?`)) return;
    try {
      setBusy(true);
      setError("");

      if (!canApprove(localUser) && newStatus === "approved") {
        alert("You don't have permission to approve. Please login as admin/accounts.");
        setBusy(false);
        return;
      }

      const poRef = doc(db, "purchaseOrders", poId);
      await updateDoc(poRef, {
        status: newStatus,
        modifiedBy: auth?.currentUser?.uid || null,
        modifiedAt: serverTimestamp(),
      });

      // Audit Trail
      await addDoc(collection(db, "auditLogs"), {
        entity: "purchaseOrder",
        entityId: poId,
        action: newStatus,
        by: auth?.currentUser?.uid || null,
        role: localUser?.role || "unknown",
        timestamp: serverTimestamp(),
      });

      // Supplier Email Alert (just log for now, could call Cloud Function)
      if (newStatus === "approved" && selectedPO?.supplierName) {
        console.log(`📧 Email alert: PO ${poId} approved for ${selectedPO.supplierName}`);
      }

      await fetchPOs();
      alert(`PO ${newStatus}`);
      closePO();
    } catch (err) {
      console.error("updatePOStatus error", err);
      setError("Update failed");
    } finally {
      setBusy(false);
    }
  };

  const displayRate = (item) => {
    if (!item) return "—";
    if (item.rate != null && item.rate !== "") return item.rate;
    if (item.stdCost != null) return item.stdCost;
    return "—";
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">
        {pendingOnly ? "Pending orders" : "Purchase Orders"}
      </h1>

      {error && <div className="text-red-600 mb-2">{error}</div>}
      {loading ? (
        <div>Loading POs…</div>
      ) : pos.length === 0 ? (
        <div className="text-gray-600">
          {pendingOnly ? "No pending orders found." : "No POs found."}
        </div>
      ) : (
        <div className="overflow-auto border rounded">
          <table className="w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-2 border">PO ID</th>
                <th className="p-2 border">PO No</th>
                <th className="p-2 border">Supplier</th>
                <th className="p-2 border">Items</th>
                <th className="p-2 border">Status</th>
                <th className="p-2 border">Created</th>
                <th className="p-2 border">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pos.map((po) => (
                <tr key={po.id} className="odd:bg-white even:bg-gray-50">
                  <td className="p-2 border break-words">{po.id}</td>
                  <td className="p-2 border">{po.poNo || "—"}</td>
                  <td className="p-2 border">{po.supplierName || "—"}</td>
                  <td className="p-2 border">{po.items?.length || 0}</td>
                  <td className="p-2 border">
                    <span
                      className={`px-2 py-0.5 rounded text-xs ${
                        po.status === "approved"
                          ? "bg-green-100 text-green-800"
                          : po.status === "rejected"
                          ? "bg-red-100 text-red-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {po.status || "—"}
                    </span>
                  </td>
                  <td className="p-2 border">{formatCreatedAt(po.createdAt)}</td>
                  <td className="p-2 border">
                    <button
                      onClick={() => openPO(po)}
                      className="px-2 py-1 mr-2 bg-indigo-600 text-white rounded"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {selectedPO && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-6">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => {
              if (!busy) closePO();
            }}
          />
          <div
            className="bg-white w-full max-w-3xl rounded shadow-lg z-50 overflow-auto"
            style={{ maxHeight: "85vh" }}
          >
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <div className="text-lg font-semibold">
                  PO: {selectedPO.poNo || selectedPO.id}
                </div>
                <div className="text-sm text-gray-600">
                  {selectedPO.supplierName || "—"}
                </div>
                <div className="text-xs text-gray-500">
                  Created: {formatCreatedAt(selectedPO.createdAt)}
                </div>
              </div>
              <button
                onClick={() => {
                  if (!busy) closePO();
                }}
                className="px-3 py-1 border rounded"
              >
                Close
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Items Table */}
              <div>
                <h3 className="font-semibold mb-2">Items</h3>
                <div className="overflow-auto border rounded">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-2 border">#</th>
                        <th className="p-2 border">Code</th>
                        <th className="p-2 border">Description</th>
                        <th className="p-2 border text-right">Qty</th>
                        <th className="p-2 border text-right">Rate</th>
                        <th className="p-2 border text-right">Stock</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedPO.items || []).map((it, idx) => (
                        <tr key={idx} className="odd:bg-white even:bg-gray-50">
                          <td className="p-2 border">{idx + 1}</td>
                          <td className="p-2 border">{it.code || "—"}</td>
                          <td className="p-2 border">{it.desc || "—"}</td>
                          <td className="p-2 border text-right">{it.qty ?? "—"}</td>
                          <td className="p-2 border text-right">{displayRate(it)}</td>
                          <td className="p-2 border text-right">{it.currentStock ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Attachments */}
              <div>
                <h3 className="font-semibold mb-2">Attachments</h3>
                {!pendingOnly && (
                  <input
                    type="file"
                    multiple
                    onChange={(e) => handleAttachmentUpload(e.target.files)}
                    disabled={busy}
                  />
                )}
                <ul className="mt-2 list-disc list-inside text-sm">
                  {(selectedPO.attachments || []).map((a, i) => (
                    <li key={i}>
                      <a
                        href={a.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline"
                      >
                        {a.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Actions */}
              {canApproveReject && (
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => updatePOStatus(selectedPO.id, "approved")}
                    disabled={busy || selectedPO.status === "approved"}
                    className={`px-3 py-1 rounded font-semibold ${
                      canApprove(localUser)
                        ? "bg-green-600 text-white hover:bg-green-700"
                        : "bg-gray-200 text-gray-600 cursor-not-allowed"
                    }`}
                  >
                    {busy ? "Working..." : "Approve"}
                  </button>
                  <button
                    onClick={() => updatePOStatus(selectedPO.id, "rejected")}
                    disabled={busy || selectedPO.status === "rejected"}
                    className={`px-3 py-1 rounded font-semibold ${
                      canApprove(localUser)
                        ? "bg-red-600 text-white hover:bg-red-700"
                        : "bg-gray-200 text-gray-600 cursor-not-allowed"
                    }`}
                  >
                    {busy ? "Working..." : "Reject"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
